import { createPromise } from './promiseUtils'
import { VIEWER_COMPONENTS_ID_PREFIXES, closest, getElementCompId } from '@wix/thunderbolt-dom-utils'

export function getCompClassType(componentType: string, uiType?: string) {
	return uiType ? `${componentType}_${uiType}` : componentType
}

const comp_type_attribute = 'data-comp-type'

export function getClosestCompIdByHtmlElement(htmlElement: HTMLElement): string {
	for (const prefix of VIEWER_COMPONENTS_ID_PREFIXES) {
		const closestElement = closest(htmlElement, `[id^="${prefix}"]`)
		if (closestElement) {
			return getElementCompId(closestElement)
		}
	}
	return ''
}

export function extractClosestCompTypeFromHtmlElement(htmlElement: HTMLElement): string {
	const closestElement = htmlElement.closest(`[${comp_type_attribute}]`)
	return closestElement?.getAttribute(comp_type_attribute) || ''
}

export type LazyComponentTrigger = 'viewport'
export type LazyComponentOptions = {
	triggers?: Array<LazyComponentTrigger>
	componentResolver: () => Promise<React.ComponentType<any>>
}
export type LazyComponentLoaderResponse = {
	componentPromise: Promise<React.ComponentType<any>>
	clearComponentListeners: () => void
}

export const isForwardRef = (Component: React.ComponentType<any>) =>
	Component.prototype &&
	Component.prototype.hasOwnProperty('$$typeof') &&
	Component.prototype.$$typeof.toString() === 'Symbol(react.forward_ref)'

export const createViewportObserver = (
	compId: string,
	getByCompId: (compId: string, doc?: Document) => HTMLElement | null
) => {
	const targetElement = getByCompId(compId)
	const { promise, resolver } = createPromise()
	if (!targetElement) {
		// If there was a mismatch (or navigation) we want to hydrate immediately and not wait for intersection
		// as the target element might be null and promise will remain hanging
		resolver()
		return {
			promise,
			cleaner: () => {},
		}
	}

	const observer = new IntersectionObserver(
		([entry]) => {
			if (entry.isIntersecting) {
				resolver()
				observer.disconnect()
			}
		},
		{ root: null }
	)
	observer.observe(targetElement)

	return {
		promise,
		cleaner: () => observer.disconnect(),
	}
}

export const getHeaderElement = (headerCompId: string | null, window: Window) => {
	let headerElement = window.document.getElementById(headerCompId || 'SITE_HEADER')
	if (!headerElement) {
		const headerElementsByTags = window.document.getElementsByTagName('header')
		if (headerElementsByTags && headerElementsByTags.length === 1) {
			headerElement = headerElementsByTags[0]
		}
	}

	return headerElement
}
